import { RuleEngine } from '@natural-apptitude/coreo-conditions';
import bbox from '@turf/bbox';
import { formatISO, startOfDay } from 'date-fns';
import validate from 'validate.js';

import { ionToCoreoDatetime } from '../../services/utils.service';
import ValidationEngine, { GeometryValidationParams } from '../../services/validation.service';
import { CoreoAssociateMap, CoreoAttribute, CoreoAttributeConditionRule, CoreoAttributeConditions, CoreoForm, CoreoRecord, CoreoRecordAttachment, CoreoRecordSyncStatus } from '../../types';
import { FormBlock, FormQuestion } from '../../types/forms.types';
import { Actions, TypeKeys } from '../actions';

import { isNewRecord } from '../../services/records.service';
import { associateSliceImmediatelyOnRemove } from './forms-associate-immediate-remove';
import { buildRecordClone } from '../records/build-record-clone';
import type { FormsUpdateRecordGeometryAction } from './forms.actions';
import { checkAssociatesReady } from './forms.ready';
import { isUkhabBaselineForm } from '../../services/ukhab/ukhab.constants';
import { clearSyncErrorAttribute, getSyncErrorMessagesForPath, parseSyncError } from '../../services/sync-error.service';

// Re-export so existing imports of checkAssociatesReady from forms.reducer keep working.
export { checkAssociatesReady };

const findAttachmentIndex = (
  attachments: CoreoRecordAttachment[],
  target: CoreoRecordAttachment
): number => {
  // Prefer stable ids. Fall back to object identity so multiple id-null clones
  // (pre-fix ghosts / in-progress edits) do not all match each other.
  const byId = attachments.findIndex(a => target.id != null && a.id === target.id);
  if (byId >= 0) {
    return byId;
  }
  return attachments.findIndex(a => a === target);
};

export interface FormSection {
  visible: boolean;
  completed: boolean;
  blocks: FormBlock[];
  constraints: any[];
}

export interface FormState extends CoreoForm {
  ready: boolean;
  dirty: boolean;
  finished: boolean;
  record: CoreoRecord;
  sections: FormSection[];
  uuid: string;
  attributes: CoreoAttribute[];
  sectionId: number;
  nextSectionId: number;
  prevSectionId: number;
  subformIdx: number;
  subformAttributeId: number;
  blocked: boolean;
}

export interface FormsState {
  forms: FormState[];
}

const initialState: FormsState = {
  forms: []
};

const emptyRecord = (): CoreoRecord => ({
  id: null,
  projectId: null,
  surveyId: null,
  state: null,
  formId: null,
  geometry: null,
  geometryCenter: null,
  feature: null,
  syncState: CoreoRecordSyncStatus.PENDING_UPDATE,
  data: {},
  // media: [],
  attachments: [],
  associates: {},
  userId: null,
  splitRecords: []
});

// Deleting previous input leaves an empty string, this should be considered undefined for validation
validate.isDefined = (value: any) => {
  return value !== null && value !== undefined && value !== '';
}

/**
 * Evaluate an individual conditional rule
 * @param block A block to evaluate (the target of a rule, not the source!)
 * @param record The record
 * @param rule A rule to evaluate
 */
export const evaluateRule = (block: FormBlock, record: CoreoRecord, rule: CoreoAttributeConditionRule): boolean => {

  if (!block) {
    // Attribute could not be found, or is not used by any question; rule is invalid
    return null;
  }

  let data: any;
  if (block.type === 'attachment' || block.type === 'media') {
    data = record.attachments.filter(a => a.attributeId === block.id);
  } else {
    data = record.data[rule.path];
  }

  return RuleEngine.evaluateRule(rule, block.type, data);
};

/**
 * Evaluate a conditional ruleset
 * @param conditions A conditional ruleset to evaluate
 * @returns The success state of the evaluation
 */
export const evaluateConditions = (conditions: CoreoAttributeConditions, attributes: CoreoAttribute[], record: CoreoRecord): boolean => {
  if (!(conditions && conditions.rules && conditions.rules.length > 0)) {
    return true;
  }

  for (const rule of conditions.rules) {

    const target = rule.path ? attributes.find(a => a.path === rule.path) as FormBlock : null;

    const result = evaluateRule(target, record, rule);
    if (result === null) {
      // Rule is invalid, ignore
      continue;
    }

    if (!result && !conditions.any) {
      // Conditions require all rules to hold, and this one failed
      return false;
    }
    if (result && conditions.any) {
      // Conditions require any rule to hold, and this one passed
      return true;
    }
  }

  // If we reach here, either:
  // - *all* rules are required and none failed: success
  // - *any* rule is required and none passed: failure
  return !conditions.any;
};

const updateAssociationSyncState = (associates: CoreoAssociateMap, attributes: CoreoAttribute[], state?: CoreoRecordSyncStatus): CoreoAssociateMap => {

  const updatedAssociates: CoreoAssociateMap = {};
  for (const attributeId in associates) {
    const attribute = attributes.find(a => a.id === parseInt(attributeId));
    if (attribute.questionType === 'child') {
      updatedAssociates[attributeId] = associates[attributeId].map(associate => ({
        ...associate,
        syncState: state ?? associate.origSyncState ?? CoreoRecordSyncStatus.PENDING_UPDATE,
        origSyncState: typeof state === 'undefined' ? null : associate.syncState,
        associates: updateAssociationSyncState(associate.associates, attributes, state)
      }));
    }
  }
  return {
    ...associates,
    ...updatedAssociates
  }
}

const markChildAssociatesSyncState = (
  associates: CoreoAssociateMap,
  attributes: CoreoAttribute[],
  syncState: CoreoRecordSyncStatus,
  attributeById: Map<number, CoreoAttribute> = new Map(attributes.map(attribute => [attribute.id, attribute]))
): CoreoAssociateMap => {
  const updatedAssociates: CoreoAssociateMap = {};
  for (const attributeId in associates) {
    const attribute = attributeById.get(parseInt(attributeId, 10));
    if (attribute && attribute.questionType === 'child') {
      updatedAssociates[attributeId] = associates[attributeId].map(associate => ({
        ...associate,
        syncState: associate.syncState === CoreoRecordSyncStatus.PENDING_DELETE ? CoreoRecordSyncStatus.PENDING_DELETE : syncState,
        associates: markChildAssociatesSyncState(associate.associates, attributes, syncState, attributeById)
      }));
    }
  }
  return {
    ...associates,
    ...updatedAssociates
  };
};

const reducer = (state = initialState, action: Actions): FormsState => {

  const isQuestion = (arg: any): arg is FormQuestion => {
    return arg.type !== null;
  };

  const processSections = (f: FormState, forms: FormsState): FormState => {
    const { record, sections, sectionId } = f;

    const formRecord: CoreoRecord = {
      ...record,
      data: {
        ...record.data
      },
      associates: {
        ...record.associates
      },
      attachments: [
        ...record.attachments
      ]
    };

    const syncError = parseSyncError(record.syncError);

    const updatedSections = sections.map((section, idx) => {

      const blocks = section.blocks.map((block: FormBlock, index: number) => {
        let isVisible = true;

        // Only evaluate conditions if it's not required
        if ((isQuestion(block)) || block.questionType === 'child' || block.questionType === 'text' || block.questionType === 'association' || block.questionType === 'geometry') {
          isVisible = evaluateConditions(block.conditions, f.attributes, formRecord);
        }

        const constraints = section.constraints[index];

        // Detect transition from hidden to visible
        const justBecameVisible = !block.isVisible && isVisible;

        // Auto-populate datetime/date fields (applies whenever visible with no data - uses falsy check)
        if ((idx === sectionId) && isVisible && block.path && !formRecord.data[block.path] && block.config?.auto) {
          switch (block.type) {
            case 'datetime': {
              if (block.config?.storeLocalTimezone === true) {
                formRecord.data[block.path] = formatISO(new Date());
              } else {
                formRecord.data[block.path] = ionToCoreoDatetime(new Date().toISOString());
              }
              break;
            }
            case 'time': {
              if (block.config?.storeLocalTimezone === true) {
                formRecord.data[block.path] = formatISO(new Date());
              } else {
                formRecord.data[block.path] = new Date().toISOString();
              }
              break;
            }
            case 'date': {
              const start = startOfDay(new Date());
              const year = start.getFullYear();
              const month = (start.getMonth() + 1).toString().padStart(2, '0');
              const date = start.getDate().toString().padStart(2, '0');
              formRecord.data[block.path] = `${year}-${month}-${date}T00:00:00.000Z`;
              break;
            }
            default: {
              console.error('DO NOT KNOW HOW TO AUTO THIS TYPE');
            }
          }
        }

        // Apply default values only when field has never been set (undefined, not just falsy)
        // This prevents re-applying defaults when user clears a field to empty string
        if ((idx === sectionId) && isVisible && block.path &&
          formRecord.data[block.path] === undefined &&
          block.config?.defaultValue !== undefined && block.config?.defaultValue !== null) {
          // Apply default value when:
          // 1. New record (any visible field gets default), OR
          // 2. Field just transitioned from hidden to visible (for existing records with conditional fields)
          // This does NOT apply to existing records where the field was already visible (user may have cleared it)
          if (isNewRecord(formRecord) || justBecameVisible) {
            formRecord.data[block.path] = block.config.defaultValue;
          }
        }

        if (!isVisible && block.path && typeof formRecord.data[block.path] !== 'undefined') {
          // In the UKHab form we deliberately inject duplicate questions to support the inline condition assessment notes
          // So here we check that if any of those other questions are still visible, do not clear the field
          const otherVisible = sections
            .reduce((acc, s) => acc.concat(s.blocks), [])
            .filter(b => b.path === block.path)
            .some(b => evaluateConditions(b.conditions, f.attributes, formRecord));

          if (!otherVisible) {
            delete formRecord.data[block.path];
          }
        } else if (!isVisible && block.path && formRecord.associates[block.id]) {
          delete formRecord.associates[block.id];
        } else if (!isVisible && block.questionType === 'geometry') {
          formRecord.geometry = null;
          formRecord.geometryCenter = null;
        } else if (!isVisible && block.type === 'attachment') {
          formRecord.attachments = formRecord.attachments.filter(a => a.attributeId !== block.id);
        }

        const blockValidationError = isVisible ? ValidationEngine.validateAnswer(block, constraints, formRecord, forms) : null;
        const blockSyncMessages = isVisible ? getSyncErrorMessagesForPath(syncError, block.path) : null;

        return {
          ...block,
          error: blockValidationError || blockSyncMessages?.join(', ') || null,
          isVisible
        };
      });

      return {
        ...section,
        blocks,
        visible: blocks.some(b => b.isVisible),
        completed: blocks.every(b => b.error === null)
      };
    });

    return {
      ...f,
      sections: updatedSections,
      record: formRecord
    }
  };

  const getNextActiveSection = (currentId: number, sections: FormSection[]): number => {
    for (let i = currentId + 1; i < sections.length; i++) {
      if (sections[i].visible) {
        return i;
      }
    }
    return null;
  };

  const getPrevActiveSection = (currentId: number, sections: FormSection[]): number => {
    for (let j = currentId - 1; j >= 0; j--) {
      if (sections[j].visible) {
        return j;
      }
    }
    return null;
  };

  const updateCurrentRecord = (_state: FormsState, cb: (record: CoreoRecord) => Partial<CoreoRecord>): FormsState => {
    const [currentForm] = _state.forms.slice(-1);

    const record = {
      ...currentForm.record,
      ...cb(currentForm.record)
    };

    const updatedForm: FormState = {
      ...currentForm,
      record
    };

    const { sections: updatedSections, record: updatedRecord } = processSections(updatedForm, _state);
    return {
      forms: [
        ..._state.forms.slice(0, -1),
        {
          ...updatedForm,
          record: updatedRecord,
          sections: updatedSections,
          nextSectionId: getNextActiveSection(updatedForm.sectionId, updatedSections),
          prevSectionId: getPrevActiveSection(updatedForm.sectionId, updatedSections),
          ready: updatedSections.every(section => section.completed),
          dirty: true
        }
      ]
    };
  };

  const initForm = (
    form: CoreoForm,
    attributes: CoreoAttribute[],
    projectId: number,
    record: CoreoRecord | null,
    subformAttributeId: number,
    subformIdx: number,
    geometryValidationParams: GeometryValidationParams,
    state?: FormState
  ): FormState => {
    const now = new Date();
    const syncState = record ? (record.syncState === CoreoRecordSyncStatus.DRAFT ? CoreoRecordSyncStatus.DRAFT : CoreoRecordSyncStatus.PENDING_UPDATE) : CoreoRecordSyncStatus.PENDING_UPDATE;
    const formRecord: CoreoRecord = record ? {
      ...record,
      syncState,
      updatedAt: now.toISOString(),
      timestamp: now.valueOf(),
      formId: record.surveyId,
      feature: record.geometry ? {
        type: 'Feature',
        geometry: record.geometry,
        bbox: bbox(record.geometry),
        properties: {}
      } : null
    } : {
      ...emptyRecord(),
      projectId,
      formId: form.id,
      surveyId: form.id,
      id: null
    };

    // Note: Default values are now applied in processSections when fields become visible
    // This ensures proper handling of conditional visibility for both new and existing records

    // Build the sections
    const sections = attributes
      .filter(a => a.visible) // There can be invisible expressions questions present in a form
      .reduce((acc: CoreoAttribute[][], attribute: CoreoAttribute) => {
        (acc[attribute.sectionIdx] = acc[attribute.sectionIdx] || []).push(attribute);
        return acc;
      }, []) // Ensure location questions have their own sections
      .reduce((acc: CoreoAttribute[][], formAttributes: CoreoAttribute[]) => {
        const geometryQuestionIdx = formAttributes.findIndex(a => a.questionType === 'geometry');
        if (geometryQuestionIdx === -1) {
          acc.push(formAttributes);
          return acc;
        }
        const newSections = [
          formAttributes.slice(0, geometryQuestionIdx),
          [formAttributes[geometryQuestionIdx]],
          formAttributes.slice(geometryQuestionIdx + 1)
        ]
        for (const section of newSections) {
          if (section.length > 0) {
            acc.push(section);
          }
        }
        return acc;
      }, [])
      .map((attr: CoreoAttribute[]): FormSection => {

        const blocks = attr.map((a: CoreoAttribute): FormBlock => {
          return {
            ...a,
            isVisible: true,
            error: null
          };
        });

        return {
          blocks,
          visible: true,
          completed: false,
          constraints: blocks.map(b => ValidationEngine.buildConstraints(b, geometryValidationParams))
        };

      });

    const newForm: FormState = {
      ...form,
      attributes,
      uuid: Math.random().toString(36).substring(2),
      ready: false,
      finished: false,
      dirty: false,
      sectionId: 0,
      nextSectionId: null,
      prevSectionId: null,
      record: formRecord,
      sections,
      subformIdx,
      subformAttributeId,
      blocked: false,
      ...(state ?? {})
    };

    const { sections: processedSections, record: processedRecord } = processSections(newForm, initialState);

    return {
      ...newForm,
      sections: processedSections,
      record: {
        ...formRecord,
        data: {
          ...formRecord.data,
          ...processedRecord.data
        }
      },
      nextSectionId: getNextActiveSection(newForm.sectionId, processedSections),
      prevSectionId: getPrevActiveSection(newForm.sectionId, processedSections),
      ready: processedSections.every(section => section.completed)
    };
  };

  switch (action.type) {
    case TypeKeys.FORM_INITIALIZE: {
      let { form, attributes, projectId, record, subformIdx, subformAttributeId, geometryValidationParams } = action;
      let noteAttributes: CoreoAttribute[] = [];

      if (isUkhabBaselineForm(form)) {
        noteAttributes = attributes.filter(a => a.label?.startsWith('noteCrit'));
        attributes = attributes.filter(a => !noteAttributes.includes(a));

        const noteAttributesMap = new Map<string, CoreoAttribute>();
        for (const attribute of noteAttributes) {
          noteAttributesMap.set(attribute.label, attribute);
        }

        const notesMapping: Record<string, string> = {
          // Rocky Shore
          'Rocky Shore A': 'noteCritA',
          'Rocky Shore B': 'noteCritB',
          'Rocky Shore C': 'noteCritC',
          'Rocky Shore D': 'noteCritD',
          'Rocky Shore E': 'noteCritE',

          'Intertidal sediment A': 'noteCritA',
          'Intertidal Sediment B': 'noteCritB',
          'Intertidal sediment C': 'noteCritC',
          'Intertidal sediment D': 'noteCritD',
          'Intertidal sediment E': 'noteCritE',

          'Seagrass A': 'noteCritA',
          'Seagrass B': 'noteCritB',
          'Seagrass C': 'noteCritC',
          'Seagrass D': 'noteCritD',
          'Seagrass E': 'noteCritE',

          'Reefs A': 'noteCritA',
          'Reefs 2': 'noteCritB',
          'Reefs C': 'noteCritC',
          'Reefs D': 'noteCritD',
          'Reefs E': 'noteCritE',

          'Saltmarsh A': 'noteCritA',
          'Saltmarsh B': 'noteCritB',
          'Saltmarsh C': 'noteCritC',
          'Saltmarsh D': 'noteCritD',
          'Saltmarsh E': 'noteCritE',
          'Saltmarsh F': 'noteCritF',

          'Hard structure A': 'noteCritA',
          'Hard structures B': 'noteCritB',
          'Hard structures C': 'noteCritC',
          'Hard structures D': 'noteCritD',
          'Hard structures E': 'noteCritE',

          'Coastal Lagoons A': 'noteCritA',
          'Coastal Lagoons B': 'noteCritB',
          'Coastal Lagoons C': 'noteCritC',
          'Coastal Lagoons D': 'noteCritD',
          'Coastal Lagoons E': 'noteCritE',
          'Coastal Lagoons F': 'noteCritF',
          'Coastal Lagoons G': 'noteCritG',
          'Coastal Lagoons H': 'noteCritH',

          'Hedge A1': 'noteCritA1',
          'Hedge A2': 'noteCritA2',
          'Hedge B1': 'noteCritB1',
          'Hedge B2': 'noteCritB2',
          'Hedge C1': 'noteCritC1',
          'Hedge C2': 'noteCritC2',
          'Hedge D1': 'noteCritD1',
          'Hedge D2': 'noteCritD2',
          'Hedgerow with trees E1': 'noteCritE1',
          'Hedgerow with trees E2': 'noteCritE2',

          'Pond A': 'noteCritA',
          'Pond B': 'noteCritB',
          'Pond C': 'noteCritC',
          'Pond D': 'noteCritD',
          'Pond E': 'noteCritE',
          'Pond F': 'noteCritF',
          'Pond G': 'noteCritG',
          'Pond H': 'noteCritH',
          'Pond I': 'noteCritI',

          'Scrub A': 'noteCritA',
          'Scrub B': 'noteCritB',
          'Scrub C': 'noteCritC',
          'Scrub D': 'noteCritD',
          'Scrub E': 'noteCritE',


          'GrasslandLow A': 'noteCritA',
          'GrasslandLow B': 'noteCritB',
          'GrasslandLow C': 'noteCritC',
          'GrasslandLow D': 'noteCritD',
          'GrasslandLow E': 'noteCritE',
          'GrasslandLow F': 'noteCritF',
          'GrasslandLow G': 'noteCritG',

          'GrasslandHigh A': 'noteCritA',
          'GrasslandHigh B': 'noteCritB',
          'GrasslandHigh C': 'noteCritC',
          'GrasslandHigh D': 'noteCritD',
          'GrasslandHigh E': 'noteCritE',
          'GrasslandHigh F': 'noteCritF',

          'Wetland A': 'noteCritA',
          'Wetland B': 'noteCritB',
          'Wetland C': 'noteCritC',
          'Wetland D': 'noteCritD',
          'Wetland E': 'noteCritE',
          'Wetland F': 'noteCritF',
          'Wetland G': 'noteCritG',
          'Wetland H': 'noteCritH',
          'Wetland I': 'noteCritI',
          'Wetland J': 'noteCritJ',

          'Heathland A': 'noteCritA',
          'Heathland B': 'noteCritB',
          'Heathland C': 'noteCritC',
          'Heathland D': 'noteCritD',
          'Heathland E': 'noteCritE',
          'Heathland F': 'noteCritF',
          'Heathland G': 'noteCritG',
          'Heathland H': 'noteCritH',
          'Heathland I': 'noteCritI',
          'Heathland J': 'noteCritJ',
          'Heathland K': 'noteCritK',

          'Woodland A': 'noteCritA',
          'Woodland B': 'noteCritB',
          'Woodland C': 'noteCritC',
          'Woodland D': 'noteCritD',
          'Woodland E': 'noteCritE',
          'Woodland F': 'noteCritF',
          'Woodland G': 'noteCritG',
          'Woodland H': 'noteCritH',
          'Woodland I': 'noteCritI',
          'Woodland Vertical J': 'noteCritJ',
          'Woodland K': 'noteCritK',
          'Woodland L': 'noteCritL',
          'Woodland M': 'noteCritM',

          'Line of trees A': 'noteCritA',
          'Line of trees B': 'noteCritB',
          'Line of trees C': 'noteCritC',
          'Line of trees D': 'noteCritD',
          'Line of trees E': 'noteCritE',

          'Tree A': 'noteCritA',
          'Tree B': 'noteCritB',
          'Tree C': 'noteCritC',
          'Tree D': 'noteCritD',
          'Tree E': 'noteCritE',
          'Tree F': 'noteCritF',

          'SVL A': 'noteCritA',
          'SVL B': 'noteCritB',
          'SVL C': 'noteCritC',
          'SVL D': 'noteCritD',

          'Urban A': 'noteCritA',
          'Urban B': 'noteCritB',
          'Urban C1': 'noteCritC1',
          'Urban C2': 'noteCritC2',
          'Urban D': 'noteCritD',
          'Urban E1': 'noteCritE1',
          'Urban E2': 'noteCritE2',
          'Urban F': 'noteCritF',
          'Urban G': 'noteCritG',

          'Limestone A': 'noteCritA',
          'Limestone B': 'noteCritB',
          'Limestone C': 'noteCritC',
          'Limestone D': 'noteCritD',
          'Limestone E': 'noteCritE',

          'Coastal A': 'noteCritA',
          'Coastal B': 'noteCritB',
          'Coastal C': 'noteCritC',
          'Coastal D': 'noteCritD',
          'Coastal E': 'noteCritE',
          'Coastal F': 'noteCritF',
          'Coastal G': 'noteCritG',
          'Coastal H': 'noteCritH',

          'WPP A': 'noteCritA',
          'WPP B': 'noteCritB',
          'WPP C': 'noteCritC',
          'WPP D': 'noteCritD',
          'WPP E': 'noteCritE',
          'WPP F': 'noteCritF',
          'WPP G': 'noteCritG',
          'WPP H': 'noteCritH',

          'Orchard Trees A': 'noteCritA',
          'Orchard B': 'noteCritB',
          'Orchard C': 'noteCritC',
          'Orchard D': 'noteCritD',
          'Orchard E': 'noteCritE',
          'Orchard F': 'noteCritF',
          'Orchard G': 'noteCritG',
          'Orchard H': 'noteCritH',

          'Ditch A': 'noteCritA',
          'Ditch B': 'noteCritB',
          'Ditch C': 'noteCritC',
          'Ditch D': 'noteCritD',
          'Ditch E': 'noteCritE',
          'Ditch F': 'noteCritF',
          'Ditch G': 'noteCritG',
          'Ditch H': 'noteCritH',
        };

        let length = attributes.length;
        for (let i = 0; i < length; i++) {
          if (attributes[i].label in notesMapping) {
            const noteAttribute = noteAttributesMap.get(notesMapping[attributes[i].label]);
            if (!noteAttribute) {
              console.error('Note attribute not found', notesMapping[attributes[i].label]);
              continue;
            }
            attributes.splice(i + 1, 0, {
              ...noteAttribute,
              conditions: attributes[i].conditions,
              sectionIdx: attributes[i].sectionIdx,
              order: attributes[i].order + 0.5
            });
            i++;
            length++;
          }
        }
      }
      const newFormState = initForm(form, attributes, projectId, record, subformAttributeId, subformIdx, geometryValidationParams);

      return {
        forms: [
          ...state.forms,
          newFormState
        ]
      };
    }

    case TypeKeys.FORM_HYDRATE: {
      const { form, attributes, projectId } = action;
      const newFormState = initForm(form, attributes, projectId, form.record, form.subformAttributeId, form.subformIdx, null, form);
      const idx = state.forms.findIndex(f => f.uuid === newFormState.uuid);
      return {
        forms: [
          ...state.forms.slice(0, idx),
          newFormState,
          ...state.forms.slice(idx + 1)
        ]
      };
    }

    case TypeKeys.FORM_RESET: {
      const [currentForm] = state.forms.slice(-1);

      const record: CoreoRecord = action.record ?? {
        ...emptyRecord(),
        projectId: currentForm.record.projectId,
        formId: currentForm.record.formId,
        surveyId: currentForm.record.surveyId,
        id: null
      };

      const resetForm = initForm(currentForm, currentForm.attributes, currentForm.record.projectId, record, currentForm.subformAttributeId, currentForm.subformIdx, null);

      return {
        forms: [
          ...state.forms.slice(0, -1),
          resetForm
        ]
      };
    }

    case TypeKeys.FORM_ADD_RECORD_ATTACHMENT: {

      return updateCurrentRecord(state, current => {
        return {
          attachments: [
            ...current.attachments,
            action.attachment
          ]
        };
      });
    }

    case TypeKeys.FORM_REPLACE_RECORD_ATTACHMENT: {

      return updateCurrentRecord(state, current => {
        const idx = findAttachmentIndex(
          current.attachments,
          action.previousAttachment ?? action.attachment
        );
        if (idx < 0) {
          return {};
        }
        return {
          attachments: [
            ...current.attachments.slice(0, idx),
            action.attachment,
            ...current.attachments.slice(idx + 1)
          ]
        };
      });
    }

    case TypeKeys.FORM_REMOVE_RECORD_ATTACHMENT: {
      return updateCurrentRecord(state, current => {
        const idx = findAttachmentIndex(current.attachments, action.attachment);
        if (idx < 0) {
          return {};
        }
        return {
          attachments: [
            ...current.attachments.slice(0, idx),
            ...current.attachments.slice(idx + 1)
          ]
        };
      })
    }

    case TypeKeys.FORM_UPDATE_RECORD_DATA:
    case TypeKeys.FORM_UPDATE_RECORD_GEOMETRY: {
      return updateCurrentRecord(state, current => {
        switch (action.type) {
          case TypeKeys.FORM_UPDATE_RECORD_DATA: {
            const { path, value } = action as any;
            return {
              data: {
                ...current.data,
                [path]: value
              },
              syncError: clearSyncErrorAttribute(current.syncError, path)
            };
          }
          case TypeKeys.FORM_UPDATE_RECORD_GEOMETRY: {
            const { geometry } = action as FormsUpdateRecordGeometryAction;
            const box = geometry ? bbox(geometry) : null;

            return {
              geometry,
              feature: geometry ? {
                type: 'Feature',
                geometry,
                bbox: box,
                properties: {}
              } : null
            };
          }
        }
      });
    }

    /**
     * Move the form forward
     */
    case TypeKeys.FORM_NEXT: {
      const [currentForm] = state.forms.slice(-1);

      const nextSectionId = currentForm.nextSectionId;
      const newNextSectionId = getNextActiveSection(nextSectionId, currentForm.sections);
      const newPrevSectionId = getPrevActiveSection(nextSectionId, currentForm.sections);

      const updatedForm = {
        ...currentForm,
        sectionId: nextSectionId,
        nextSectionId: newNextSectionId,
        prevSectionId: newPrevSectionId
      };
      // Run Process Sections to initiate any data
      const { record, sections } = processSections(updatedForm, state);

      return {
        forms: [
          ...state.forms.slice(0, -1),
          {
            ...updatedForm,
            record,
            sections,
            nextSectionId: getNextActiveSection(updatedForm.sectionId, sections),
            prevSectionId: getPrevActiveSection(updatedForm.sectionId, sections),
            ready: sections.every(section => section.completed),
            blocked: false
          }
        ]
      };
    }

    /**
     * Move the form back
     */
    case TypeKeys.FORM_PREV: {
      const [currentForm] = state.forms.slice(-1);
      const prevSectionId = currentForm.prevSectionId;
      const newNextSectionId = getNextActiveSection(prevSectionId, currentForm.sections);
      const newPrevSectionId = getPrevActiveSection(prevSectionId, currentForm.sections);

      return {
        forms: [
          ...state.forms.slice(0, -1),
          {
            ...currentForm,
            sectionId: prevSectionId,
            nextSectionId: newNextSectionId,
            prevSectionId: newPrevSectionId,
            blocked: false
          }
        ]
      };
    }

    case TypeKeys.FORM_CLEAR_DIRTY: {
      const [currentForm] = state.forms.slice(-1);
      return {
        forms: [
          ...state.forms.slice(0, -1),
          {
            ...currentForm,
            dirty: false
          }
        ]
      };
    }

    case TypeKeys.FORM_DISMISS: {
      return {
        forms: state.forms.slice(0, -1)
      };
    }

    case TypeKeys.FORM_COMPLETE: {
      const currentForm = state.forms[state.forms.length - 1];

      if (!currentForm) {
        console.warn('FORM_COMPLETE dispatched but no current form found');
        return state;
      }

      const forms = state.forms.slice(0, -1);

      if (!currentForm.subformAttributeId) {
        return { forms };
      }

      // The form underneath must own the child question, otherwise the stack is corrupt
      // (e.g. the same child opened twice) and merging would write into the wrong record.
      const targetForm = forms[forms.length - 1];
      if (!targetForm?.attributes?.some(a => a.id === currentForm.subformAttributeId)) {
        console.error('FORM_COMPLETE: parent form does not own subform attribute; discarding merge', {
          subformAttributeId: currentForm.subformAttributeId,
          subformIdx: currentForm.subformIdx,
          targetFormId: targetForm?.id,
          stackDepth: state.forms.length
        });
        return { forms };
      }

      const computeChildSyncState = (): CoreoRecordSyncStatus => {
        const childCheck = checkAssociatesReady(currentForm.record.associates);
        let next =
          currentForm.ready && childCheck
            ? CoreoRecordSyncStatus.PENDING_UPDATE
            : CoreoRecordSyncStatus.INCOMPLETE;
        return next;
      };

      // If we are adding a associated record
      if (typeof currentForm.subformIdx === 'undefined') {

        return updateCurrentRecord({ forms }, current => {
          // Split records are added as associates, clean the currentForm record of them when updating and add them if any
          const { splitRecords, ...rest } = currentForm.record;
          const currentFormRecord = {...rest};

          const childSyncState = computeChildSyncState();
          const associates = {
            ...current.associates,
            [currentForm.subformAttributeId]: [
              ...(current.associates[currentForm.subformAttributeId] || []),
              {
                ...currentFormRecord,
                syncState: childSyncState
              },
              // If there are split records then add them as associates
              ...(splitRecords ?? [])
            ]
          };
          return { associates };
        });
      }

      // Else we're updating an existing associated record
      return updateCurrentRecord({ forms }, current => {
        // Split records are added as associates, clean the currentForm record of them when updating and add them if any
        const { splitRecords, ...rest } = currentForm.record;
        const currentFormRecord = {...rest};

        const childSyncState = computeChildSyncState();
        const existing = current.associates[currentForm.subformAttributeId] || [];
        const associates = {
          ...current.associates,
          [currentForm.subformAttributeId]: [
            ...existing.slice(0, currentForm.subformIdx),
            {
              ...currentFormRecord,
              syncState: childSyncState
            },
            ...existing.slice(currentForm.subformIdx + 1),
            // If there are split records then add them as associates
            ...(splitRecords ?? [])
          ]
        };
        return { associates };
      });
    }

    case TypeKeys.FORM_FINISHED: {
      const [currentForm] = state.forms.slice(-1);
      return {
        forms: [
          ...state.forms.slice(0, -1),
          {
            ...currentForm,
            finished: true
          }
        ]
      };
    }

    case TypeKeys.FORM_DISMISS_ALL: {
      return { forms: [] };
    }

    case TypeKeys.FORM_ADD_RECORD_ASSOCIATE:
    case TypeKeys.FORM_ADD_RECORD_ASSOCIATED_TO: {

      const { associationAttributeId, record } = action;
      return updateCurrentRecord(state, current => {
        return {
          associates: {
            ...current.associates,
            [associationAttributeId]: [
              ...(current.associates[associationAttributeId] || []),
              record
            ]
          }
        };
      });
    }

    case TypeKeys.FORM_UPDATE_RECORD_ASSOCIATE: {
      const { associationAttributeId, record, index } = action;

      return updateCurrentRecord(state, current => {
        return {
          associates: {
            ...current.associates,
            [associationAttributeId]: [
              ...current.associates[associationAttributeId].slice(0, index),
              record,
              ...current.associates[associationAttributeId].slice(index + 1)
            ]
          }
        };
      });
    }

    case TypeKeys.FORM_REMOVE_RECORD_ASSOCIATE: {
      const { associationAttributeId, index, attributes } = action;

      return updateCurrentRecord(state, current => {

        // Staging-only associate (no DB row yet): slice out immediately. Persisted rows use
        // PENDING_DELETE until save so Cancel can discard Redux without orphaning SQLite.
        const existingAssociate = current.associates[associationAttributeId][index];
        const immediatelyDelete = associateSliceImmediatelyOnRemove(existingAssociate);
        if (immediatelyDelete) {
          return {
            associates: {
              ...current.associates,
              [associationAttributeId]: [
                ...current.associates[associationAttributeId].slice(0, index),
                ...current.associates[associationAttributeId].slice(index + 1)
              ]
            }
          }
        }

        const newAssociate: CoreoRecord = {
          ...existingAssociate,
          syncState: CoreoRecordSyncStatus.PENDING_DELETE,
          origSyncState: existingAssociate.syncState,
          associates: updateAssociationSyncState(existingAssociate.associates, attributes, CoreoRecordSyncStatus.PENDING_DELETE)
        };

        return {
          associates: {
            ...current.associates,
            [associationAttributeId]: [
              ...current.associates[associationAttributeId].slice(0, index),
              newAssociate,
              ...current.associates[associationAttributeId].slice(index + 1)
            ]
          }
        };
      });
    }


    case TypeKeys.FORM_RESTORE_RECORD_ASSOCIATE: {
      const { associationAttributeId, index, attributes } = action;

      return updateCurrentRecord(state, current => {

        const newAssociate: CoreoRecord = {
          ...current.associates[associationAttributeId][index],
          syncState: current.associates[associationAttributeId][index].origSyncState,
          origSyncState: null,
          associates: updateAssociationSyncState(current.associates[associationAttributeId][index].associates, attributes)
        };

        return {
          associates: {
            ...current.associates,
            [associationAttributeId]: [
              ...current.associates[associationAttributeId].slice(0, index),
              newAssociate,
              ...current.associates[associationAttributeId].slice(index + 1)
            ]
          }
        };
      });
    }

    case TypeKeys.FORM_CLONE_RECORD_ASSOCIATE: {

      const { associationAttributeId, index, userId, attributes } = action;
      return updateCurrentRecord(state, current => {
        const clone = buildRecordClone(
          current.associates[associationAttributeId][index],
          current.updatedAt,
          userId,
          current.state,
          attributes
        );
        return {
          associates: {
            ...current.associates,
            [associationAttributeId]: [
              ...current.associates[associationAttributeId].slice(0, index + 1),
              clone,
              ...current.associates[associationAttributeId].slice(index + 1)
            ]
          }
        }
      });
    }

    case TypeKeys.FORM_ADD_RECORD_ASSOCIATED_TO: {
      const { associationAttributeId, record } = action;
      return updateCurrentRecord(state, current => {
        return {
          associates: {
            ...current.associates,
            [associationAttributeId]: [
              ...(current.associates[associationAttributeId] || []),
              record
            ]
          }
        };
      });
    }

    case TypeKeys.FORM_REMOVE_RECORD_ASSOCIATED_TO: {
      const { associationAttributeId, index } = action;
      return updateCurrentRecord(state, current => {
        return {
          associates: {
            ...current.associates,
            [associationAttributeId]: [
              ...current.associates[associationAttributeId].slice(0, index),
              ...current.associates[associationAttributeId].slice(index + 1)
            ]
          }
        };
      });
    }

    case TypeKeys.FORM_SPLIT_RECORD: {
      const { newGeometry: geometry, newRecords, updatedAssociates } = action;

      const currentForm = state.forms[state.forms.length - 1];
      // Set new records sync status based on the current form state and their associates readiness
      const splitRecords = (newRecords ?? []).map(r => {
        return {
          ...r,
          syncState: currentForm.ready && checkAssociatesReady(r.associates) ? CoreoRecordSyncStatus.PENDING_UPDATE : CoreoRecordSyncStatus.DRAFT
        }
      });

      /** Update current record with new geometry and updated associates */
      return updateCurrentRecord(state, _current => {
        const box = geometry ? bbox(geometry) : null;

        return {
          geometry,
          feature: geometry ? {
            type: 'Feature',
            geometry,
            bbox: box,
            properties: {}
          } : null,
          associates: updatedAssociates,
          splitRecords
        };
      });
    }

    case TypeKeys.FORM_MARK_AS_READY: {
      const [currentForm] = state.forms.slice(-1);
      if (!currentForm) {
        return state;
      }
      return updateCurrentRecord(state, current => ({
        syncState: CoreoRecordSyncStatus.PENDING_UPDATE,
        associates: markChildAssociatesSyncState(current.associates, action.attributes, CoreoRecordSyncStatus.PENDING_UPDATE)
      }));
    }

    case TypeKeys.FORM_BLOCK: {
      const [currentForm] = state.forms.slice(-1);
      return {
        forms: [
          ...state.forms.slice(0, -1),
          {
            ...currentForm,
            blocked: true
          }
        ]
      };
    }

    case TypeKeys.FORM_UNBLOCK: {
      const [currentForm] = state.forms.slice(-1);
      return {
        forms: [
          ...state.forms.slice(0, -1),
          {
            ...currentForm,
            blocked: false
          }
        ]
      };
    }

    default: {
      return state;
    }
  }
};

export default reducer;
